import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  HelpCircle,
  Loader2,
  LogOut,
  Mail,
  MousePointer,
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import moment from 'moment';
import { cn } from '../../../utils/cn';
import { useToast } from '../../../contexts/ToastContext';
import { getContactEmailsRequest } from '../../../store/contacts';
import { getCampaignsRequest, selectCampaigns } from '../../../store/campaigns';
import {
  clearSendEmailState,
  selectSendEmailError,
  selectSendEmailLoading,
  selectSendEmailMessage,
  selectSendEmailSuccess,
  sendEmailRequest,
} from '../../../store/emailSentHistory';
import { IEmailSentHistory } from '../../../types/emailSentHistory/emailSentHistory.types';
import { ICampaign } from '../../../types/campaigns/campaigns.types';
import { Link } from 'react-router-dom';
import TableSkeleton from '../../../skeletons/TableSkeleton';

interface ContactEmailsTabProps {
  contactId: string;
  status: 'sent' | 'draft' | 'failed';
}

const ContactEmailsTab: React.FC<ContactEmailsTabProps> = ({
  contactId,
  status,
}) => {
  const dispatch = useDispatch<any>();
  const { showToast } = useToast();
  const contactEmailsRaw = useSelector(
    (state: RootState) => state.contacts?.contactEmails
  );
  const contactEmails = Array.isArray(contactEmailsRaw) ? contactEmailsRaw : [];
  const loading = useSelector(
    (state: RootState) => state.contacts?.contactEmailsLoading || false
  );
  const error = useSelector(
    (state: RootState) => state.contacts?.contactEmailsError || null
  );
  const paginationRaw = useSelector(
    (state: RootState) => state.contacts?.contactEmailsPagination
  );
  const pagination = useMemo(
    () =>
      paginationRaw || {
        page: 0,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
    [paginationRaw]
  );

  const campaigns = useSelector(selectCampaigns);
  const sendLoading = useSelector(selectSendEmailLoading);
  const sendError = useSelector(selectSendEmailError);
  const sendSuccess = useSelector(selectSendEmailSuccess);
  const sendMessage = useSelector(selectSendEmailMessage);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;

      if (hasPaginationData) {
        if (currentPage !== pagination.page || pageSize !== pagination.limit) {
          setCurrentPage(pagination.page);
          setPageSize(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, currentPage, pageSize]);

  // Reset pagination when contactId or status changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
    }
  }, [contactId, status, isInitialized]);

  // Reset to first page when page size changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(0);
    }
  }, [pageSize, isInitialized]);

  // Fetch campaigns when component mounts
  useEffect(() => {
    if (contactEmails.length > 0) {
      dispatch(getCampaignsRequest());
    }
  }, [dispatch, contactEmails.length]);

  // Fetch contact emails when component mounts or contactId/status changes
  useEffect(() => {
    if (isInitialized && contactId) {
      const filters = {
        page: currentPage,
        limit: pageSize,
        status: status,
      };
      dispatch(getContactEmailsRequest(contactId, filters));
    }
  }, [dispatch, contactId, status, currentPage, pageSize, isInitialized]);

  useEffect(() => {
    if (sendSuccess) {
      showToast({
        type: 'success',
        title: 'Email Sent',
        message: sendMessage || 'Email resent successfully.',
      });

      if (contactId) {
        const filters = {
          page: currentPage,
          limit: pageSize,
          status,
        };
        dispatch(getContactEmailsRequest(contactId, filters));
      }

      dispatch(clearSendEmailState());
    }

    if (sendError) {
      showToast({
        type: 'error',
        title: 'Resend Failed',
        message: sendError,
      });
      dispatch(clearSendEmailState());
    }
  }, [
    sendSuccess,
    sendError,
    sendMessage,
    showToast,
    dispatch,
    contactId,
    currentPage,
    pageSize,
    status,
  ]);

  // Helper function to determine status from email history record
  const getEmailStatus = (emailHistory: IEmailSentHistory): string => {
    if (emailHistory.error || emailHistory.event?.failed) return 'failed';
    if (emailHistory.complaintAt) return 'complaint';
    if (emailHistory.unsubscribedAt) return 'unsubscribed';
    if (emailHistory.spamAt) return 'spam';
    if (emailHistory.bouncedAt) return 'bounced';
    if (emailHistory.clickedEvents && emailHistory.clickedEvents.length > 0)
      return 'clicked';
    if (
      emailHistory.humanOpenedAt ||
      (emailHistory.openedEvents && emailHistory.openedEvents.length > 0)
    )
      return 'opened';
    if (emailHistory.deliveredAt) return 'delivered';
    if (emailHistory.sentAt) return 'sent';
    return 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'bg-success-background text-success border-success/30';
      case 'opened':
        return 'bg-primary-background text-primary border-primary/30';
      case 'clicked':
        return 'bg-info-background text-info border-info/30';
      case 'bounced':
      case 'spam':
      case 'failed':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'complaint':
      case 'draft':
      case 'unsubscribed':
        return 'bg-warning-background text-warning border-warning/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle2 className="w-3.5 h-3.5" />;
      case 'opened':
        return <Eye className="w-3.5 h-3.5" />;
      case 'clicked':
        return <MousePointer className="w-3.5 h-3.5" />;
      case 'bounced':
      case 'complaint':
      case 'failed':
      case 'spam':
        return <AlertCircle className="w-3.5 h-3.5" />;
      case 'unsubscribed':
        return <LogOut className="w-3.5 h-3.5" />;
      case 'draft':
        return <Clock className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'opened':
        return 'Opened';
      case 'clicked':
        return 'Clicked';
      case 'bounced':
        return 'Bounced';
      case 'complaint':
        return 'Complaint';
      case 'unsubscribed':
        return 'Unsubscribed';
      case 'spam':
        return 'Spam';
      case 'failed':
        return 'Failed';
      case 'draft':
        return 'Draft';
      default:
        return 'Pending';
    }
  };

  // Helper function to format date using moment
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';

    const momentDate = moment(date);
    const now = moment();

    // Check if date is valid
    if (!momentDate.isValid()) return 'Invalid date';

    // Show relative time for recent dates
    if (momentDate.isAfter(now.subtract(1, 'hour'))) {
      return momentDate.fromNow();
    }

    // Show "Today" for today's dates
    if (momentDate.isSame(now, 'day')) {
      return `Today at ${momentDate.format('h:mm A')}`;
    }

    // Show "Yesterday" for yesterday's dates
    if (momentDate.isSame(now.subtract(1, 'day'), 'day')) {
      return `Yesterday at ${momentDate.format('h:mm A')}`;
    }

    // Show date and time for older dates
    if (momentDate.isSame(now, 'year')) {
      return momentDate.format('MMM D, h:mm A');
    }

    // Show full date for dates from different years
    return momentDate.format('MMM D, YYYY h:mm A');
  };

  // Helper function to get action text
  const getActionText = (emailHistory: IEmailSentHistory): string => {
    const emailStatus = getEmailStatus(emailHistory);
    switch (emailStatus) {
      case 'clicked':
        return `Email clicked (${emailHistory.clickedEvents?.length || 0} times)`;
      case 'opened':
        return `Email opened (${emailHistory.openedEvents?.length || 0} times)`;
      case 'bounced':
        return 'Email bounced';
      case 'complaint':
        return 'Complaint received';
      case 'unsubscribed':
        return 'Contact unsubscribed';
      case 'spam':
        return 'Marked as spam';
      case 'failed':
        return 'Failed to send';
      case 'delivered':
        return 'Email delivered';
      case 'draft':
        return 'Email draft created';
      default:
        return 'Email sent';
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleResendEmail = (
    e: React.MouseEvent<HTMLButtonElement>,
    emailId?: string
  ) => {
    e.stopPropagation();
    if (!emailId) return;
    dispatch(sendEmailRequest(emailId));
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Check if row is expanded
  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  // Helper function to get campaign name by ID
  const getCampaignName = (campaignId: string | undefined): string => {
    if (!campaignId || !campaigns || campaigns.length === 0) {
      return 'N/A';
    }
    const campaign = campaigns.find(
      (camp: ICampaign) => camp._id === campaignId
    );
    return campaign?.name || campaignId;
  };

  // Helper function to format delivery timeline
  const getDeliveryTimeline = (emailHistory: IEmailSentHistory): string[] => {
    const timeline: string[] = [];

    if (emailHistory.createdAt) {
      timeline.push(`Created ${formatDate(emailHistory.createdAt)}`);
    }

    if (emailHistory.sentAt) {
      timeline.push(`Sent ${formatDate(emailHistory.sentAt)}`);
    }

    // Show apiResult.message if available, otherwise show error
    if (emailHistory.apiResult?.message) {
      timeline.push(`Failed: ${emailHistory.apiResult.message}`);
    } else if (emailHistory.error) {
      timeline.push(`Failed: ${emailHistory.error}`);
    }

    if (emailHistory.deliveredAt) {
      timeline.push(`Delivered ${formatDate(emailHistory.deliveredAt)}`);
    }

    if (emailHistory.openedEvents && emailHistory.openedEvents.length > 0) {
      timeline.push(
        `Opened ${emailHistory.openedEvents.length} time${emailHistory.openedEvents.length > 1 ? 's' : ''}`
      );
    }

    if (emailHistory.clickedEvents && emailHistory.clickedEvents.length > 0) {
      timeline.push(
        `Clicked ${emailHistory.clickedEvents.length} time${emailHistory.clickedEvents.length > 1 ? 's' : ''}`
      );
    }

    return timeline;
  };

  // Helper function to get error message
  const getErrorMessage = (emailHistory: IEmailSentHistory): string => {
    if (emailHistory.apiResult?.message) {
      return emailHistory.apiResult.message;
    }
    if (emailHistory.error) {
      return emailHistory.error;
    }
    return 'Error message is not available';
  };

  return (
    <div className="flex-1 p-4">
      {/* Messages Table */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {loading ? (
          <TableSkeleton columns={4} rows={10} className="border-0" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  {status === 'draft' ? 'Date Created' : 'Date Sent'}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  Campaign
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <HelpCircle className="w-3 h-3 text-muted-foreground" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center space-x-3 text-destructive">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Error loading emails: {error}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : contactEmails.length > 0 ? (
                contactEmails.map(
                  (emailHistory: IEmailSentHistory, index: number) => {
                    const emailStatus = getEmailStatus(emailHistory);
                    const actionText = getActionText(emailHistory);
                    const displayDate = formatDate(
                      emailHistory.sentAt || emailHistory.createdAt
                    );
                    const rowId =
                      emailHistory._id ||
                      emailHistory.messageId ||
                      `row-${index}`;
                    const isExpanded = isRowExpanded(rowId);
                    const timeline = getDeliveryTimeline(emailHistory);

                    return (
                      <React.Fragment key={rowId}>
                        {/* Main Row */}
                        <tr
                          className={cn(
                            'border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer'
                          )}
                          onClick={() => toggleRowExpansion(rowId)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(rowId);
                                }}
                                className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                )}
                              </button>
                              <span className="text-sm text-card-foreground">
                                {displayDate}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-sm text-card-foreground truncate">
                                {actionText}
                              </span>
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {emailHistory.campaignId ? (
                                <Link
                                  to={`/campaigns/workflow/${emailHistory.campaignId}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-sm text-primary hover:text-primary-hover cursor-pointer truncate hover:underline"
                                >
                                  {getCampaignName(emailHistory.campaignId)}
                                </Link>
                              ) : (
                                <span className="text-sm text-muted-foreground truncate">
                                  {getCampaignName(emailHistory.campaignId)}
                                </span>
                              )}
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium border',
                                getStatusColor(emailStatus)
                              )}
                            >
                              {getStatusIcon(emailStatus)}
                              <span>{getStatusText(emailStatus)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {(status === 'draft' || status === 'failed') &&
                            emailHistory._id ? (
                              <button
                                type="button"
                                onClick={(e) =>
                                  handleResendEmail(e, emailHistory._id)
                                }
                                disabled={sendLoading}
                                className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-md hover:bg-primary/20 hover:text-primary-hover transition-colors"
                              >
                                {sendLoading ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <>
                                    <Mail className="w-3.5 h-3.5" />
                                    <span>Resend</span>
                                  </>
                                )}
                              </button>
                            ) : emailHistory._id ? (
                              <Link
                                to={`/emails/view/${emailHistory._id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-md hover:bg-primary/20 hover:text-primary-hover transition-colors"
                              >
                                <ArrowRight className="w-3.5 h-3.5" />
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-md opacity-60 cursor-not-allowed"
                                disabled
                              >
                                <ArrowRight className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="px-4 py-3 bg-muted/50">
                              <div className="pl-6 space-y-3">
                                {/* Campaign Information */}
                                <div className="flex items-center space-x-4">
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[120px]">
                                    CAMPAIGN:
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    {emailHistory.campaignId ? (
                                      <Link
                                        to={`/campaigns/workflow/${emailHistory.campaignId}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-sm text-primary hover:text-primary-hover font-medium transition-colors"
                                      >
                                        {getCampaignName(
                                          emailHistory.campaignId
                                        )}
                                      </Link>
                                    ) : (
                                      <span className="text-sm text-gray-500">
                                        N/A
                                      </span>
                                    )}
                                    <ChevronRight className="w-3 h-3 text-gray-400" />
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    Created {formatDate(emailHistory.createdAt)}
                                  </span>
                                </div>

                                {/* Error Information */}
                                {status === 'failed' && (
                                  <div className="flex items-start space-x-4">
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[120px]">
                                      ERROR:
                                    </span>
                                    <div className="flex-1">
                                      <div className="bg-destructive-background border border-destructive/30 rounded-md p-3">
                                        <p className="text-sm text-destructive font-medium">
                                          {getErrorMessage(emailHistory)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Delivery Timeline */}
                                {timeline.length > 0 && (
                                  <div className="flex items-start space-x-4">
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide min-w-[120px]">
                                      TIMELINE:
                                    </span>
                                    <div className="space-y-1">
                                      {timeline.map((item, idx) => (
                                        <div
                                          key={idx}
                                          className="text-sm text-gray-700"
                                        >
                                          {item}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }
                )
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Mail className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold text-card-foreground mb-1">
                        No {status} emails
                      </h3>
                      <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                        No {status} emails found for this contact.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading &&
        !error &&
        contactEmails.length > 0 &&
        pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  {pagination.page * pagination.limit + 1}-
                  {Math.min(
                    (pagination.page + 1) * pagination.limit,
                    pagination.total
                  )}{' '}
                  of {pagination.total}
                </div>

                {/* Page Limit Dropdown */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={pagination.page === 0}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <span className="text-sm text-gray-500 px-3 py-1">
                  Page {pagination.page + 1} of {pagination.totalPages}
                </span>

                <button
                  className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={pagination.page >= pagination.totalPages - 1}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ContactEmailsTab;
